import { useEffect, useState } from 'react';
import { Alert } from 'react-native';
import { translateCurrent } from '../i18n';

// Central, module-level broadcast of "is this account currently restricted
// to read-only browsing because it's an unverified email/password 'member'
// account". Firestore rules are the actual security boundary (see
// firestore.rules' canWriteSocial()) — this is purely a client-side UX
// convenience so deeply-nested write hooks (useVenueVibes.ts, useVibes.ts,
// usePlanJoinRequest.ts, chat send helpers, etc.) can show a friendly gate
// instead of letting the write silently fail with a permission-denied error.
// AppNavigator is the one place that has both Auth state (emailVerified) and
// the Firestore profile (role) needed to compute this, so it's the sole
// writer via setSocialWriteRestricted(); everything else only reads.
let restricted = false;
const listeners = new Set<(value: boolean) => void>();

export function setSocialWriteRestricted(next: boolean): void {
  if (restricted === next) return;
  restricted = next;
  listeners.forEach(listener => listener(restricted));
}

export function isSocialWriteRestricted(): boolean {
  return restricted;
}

export function useSocialWriteRestricted(): boolean {
  const [value, setValue] = useState(restricted);
  useEffect(() => {
    setValue(restricted);
    listeners.add(setValue);
    return () => {
      listeners.delete(setValue);
    };
  }, []);
  return value;
}

/**
 * Guard for a social/write action triggered directly from a button handler
 * that does NOT already wrap the call in try/catch. Returns true if the
 * action may proceed; otherwise shows a friendly "verify your email" alert
 * and returns false.
 */
export function requireVerifiedEmailForAction(): boolean {
  if (!restricted) return true;
  Alert.alert(
    translateCurrent('verifyEmail.gateTitle'),
    translateCurrent('verifyEmail.gateMessage'),
  );
  return false;
}

/**
 * Guard for use at the top of a shared write function (sendChatMessage,
 * sendVibeRequest, acceptVibeRequest, unvibeUsers, toggleFollow, toggleLike,
 * addComment, joinEventChat, setActivityInterested, plan-join sendRequest,
 * etc.). Throws so the call site's existing try/catch + Alert.alert(message)
 * pattern (already used everywhere these functions are called) surfaces the
 * message without every call site needing its own gate check.
 */
export function assertSocialWriteAllowed(): void {
  if (restricted) {
    throw new Error(translateCurrent('verifyEmail.gateMessage'));
  }
}
